import React from 'react';

// Pretty labels for the feature keys returned by GET /api/academy/usage/.
const FEATURE_LABELS = {
  tutor_explanation: 'Tutor explanations',
  quiz_generation: 'Quiz generation',
  quiz_grading: 'Quiz grading',
  trial_session: 'Mock trial sessions',
  trial_turn: 'Mock trial turns',
};

const Bar = ({ used, limit }) => {
  // limit can be null/undefined for "unlimited"; guard division.
  const hasLimit = typeof limit === 'number' && limit > 0;
  const pct = hasLimit ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  const color = pct >= 90 ? 'bg-red-500' : pct >= 70 ? 'bg-amber-500' : 'bg-primary-500';
  return (
    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
      <div className={`h-2 rounded-full ${hasLimit ? color : 'bg-gray-300 dark:bg-gray-600'}`} style={{ width: `${pct}%` }} />
    </div>
  );
};

/**
 * Renders the fair-use quota summary from GET /api/academy/usage/.
 * Shape: { verified: bool, features: { <feature>: { used, limit, remaining } } }
 */
const UsageMeter = ({ usage, className = '' }) => {
  if (!usage) return null;
  const features = usage.features || {};
  const keys = Object.keys(features);

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-lg shadow dark:shadow-gray-700/10 p-5 ${className}`}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white">Fair-use usage</h3>
        {usage.verified ? (
          <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
            Verified — higher limits
          </span>
        ) : (
          <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
            Unverified
          </span>
        )}
      </div>
      {keys.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">No usage recorded yet.</p>
      ) : (
        <ul className="space-y-3">
          {keys.map((key) => {
            const f = features[key] || {};
            const unlimited = !(typeof f.limit === 'number' && f.limit > 0);
            return (
              <li key={key}>
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="text-gray-700 dark:text-gray-300">{FEATURE_LABELS[key] || key}</span>
                  <span className="text-gray-500 dark:text-gray-400">
                    {unlimited ? `${f.used ?? 0} used` : `${f.used ?? 0} / ${f.limit}`}
                  </span>
                </div>
                <Bar used={f.used ?? 0} limit={f.limit} />
              </li>
            );
          })}
        </ul>
      )}
      {!usage.verified && (
        <p className="mt-4 text-xs text-gray-500 dark:text-gray-400">
          Verify your academic email in your profile to raise these limits.
        </p>
      )}
    </div>
  );
};

export default UsageMeter;
